import { ValidatorFn } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationService } from './../../services/authentication.service';
import { EncryptionService } from './../../services/encryption.service';
import { confirmPasswordValidation } from '../pre-login/pre-login.util';

@Component({
  selector: 'change-password-component',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  isCurrentPasswordVisible = false;
  isNewPasswordVisible = false;
  isConfirmPasswordVisible = false;
  passwordLength = 16;

  changePasswordFormGroup: FormGroup;

  currentPasswordValueChangesSubscription: Subscription;
  newPasswordValueChangesSubscription: Subscription;
  existingValidators: ValidatorFn[] = [];

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private dialogRef: MatDialogRef<ChangePasswordComponent>,
    private encryptionService: EncryptionService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      showCancelButton: boolean;
      message: string;
      [key: string]: unknown;
    }
  ) {
    const newPasswordFormControl = new FormControl(null, [Validators.required]);
    this.changePasswordFormGroup = new FormGroup({
      currentPassword: new FormControl(null, [Validators.required]),
      newPassword: newPasswordFormControl,
      confirmPassword: new FormControl(null, [
        Validators.required,
        confirmPasswordValidation(newPasswordFormControl),
      ]),
    });
  }

  ngOnInit(): void {
    this.existingValidators = [
      (control: FormControl) =>
        control?.value === this.currentPasswordFormControl?.value
          ? { sameAsOldPassword: true }
          : null,
    ];
    this.currentPasswordValueChangesSubscription = this.currentPasswordFormControl?.valueChanges?.subscribe(
      () => {
        this.newPasswordFormControl?.setValue(
          this.newPasswordFormControl?.value
        );
      }
    );

    this.newPasswordValueChangesSubscription = this.newPasswordFormControl?.valueChanges?.subscribe(
      () => {
        this.confirmPasswordFormControl?.setValue(
          this.confirmPasswordFormControl?.value
        );
      }
    );
  }

  ngOnDestroy(): void {
    this.newPasswordValueChangesSubscription?.unsubscribe();
  }

  onResetClick(): void {
    this.changePasswordFormGroup.reset();
  }

  onSaveClick(): void {
    if (
      !this.currentPasswordFormControl?.value ||
      !this.newPasswordFormControl?.value ||
      !this.confirmPasswordFormControl?.value
    )
      return;

    const salt = this.encryptionService.generateSalt();

    const {
      cipherText: encryptedOldPassword,
    } = this.encryptionService?.encrypt(
      this.currentPasswordFormControl?.value,
      salt
    );
    const {
      cipherText: encryptedNewPassword,
    } = this.encryptionService?.encrypt(
      this.newPasswordFormControl?.value,
      salt
    );

    const dataForPut = {
      oldPassword: encryptedOldPassword,
      newPassword: encryptedNewPassword,
      saltKey: salt,
      isPasswordReset: this.authenticationService.loggedInUserSubject.getValue()
        .isResetPasswordAllowed
        ? true
        : false,
    };

    this.http
      .put(`/security/change-password`, dataForPut)
      .pipe(
        map((response: any) => response?.data),
        catchError((error) => {
          throw error;
        })
      )
      .subscribe(
        (data) => {
          this.dialogRef?.close(data);
          this.authenticationService?.logout();
        },
        (error) => {
          console.error('Error while changing password: ', error);
        }
      );
  }

  get currentPasswordFormControl(): FormControl {
    return this.changePasswordFormGroup?.controls
      ?.currentPassword as FormControl;
  }

  get newPasswordFormControl(): FormControl {
    return this.changePasswordFormGroup?.controls?.newPassword as FormControl;
  }

  get confirmPasswordFormControl(): FormControl {
    return this.changePasswordFormGroup?.controls
      ?.confirmPassword as FormControl;
  }

  get errorMessageForNewPassword() {
    if (
      this.newPasswordFormControl?.value &&
      this.newPasswordFormControl?.hasError('sameAsOldPassword')
    )
      return 'New Password is same as Current Password.';
    else return 'Please enter valid New Password.';
  }

  get errorMessageForConfirmPassword() {
    if (this.confirmPasswordFormControl?.hasError('required'))
      return 'Please enter Confirm Password.';
    else if (this.confirmPasswordFormControl?.hasError('confirmpassword'))
      return 'The passwords do not match.';
  }

  toggleIsNewPassword() {
    this.isNewPasswordVisible = !this.isNewPasswordVisible;
  }
}
